/**
 * Attorney RoR Coverage-Analysis Memo Generator (internal work product)
 *
 * Surfaces the existing /attorney-tools/ror-analysis/* backend (no prior UI).
 * Internal attorney work product analyzing a RECEIVED Reservation of Rights:
 * per reserved ground — likely policy basis, the insured's position, rebutting
 * evidence, a strength scaffold (attorney-input) — plus the engine-sourced dollar
 * exposure if the reservation ripens into a denial, and a recommended posture.
 * Companion to the outbound RoR Response generator. Mirrors that page's pattern.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, FileSearch, ShieldAlert, AlertTriangle, Copy, Download, Check, Loader2, Lock,
} from 'lucide-react';
import { responseErrorMessage } from '../services/generationGate';
import AttorneyLayout from '../components/AttorneyLayout';
import { getAuthHeaders } from '../lib/authHeaders';
import { FinancialValue } from '../components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DEPTHS = [
  { type: 'full', name: 'Full Analysis', description: 'Per-ground analysis + exposure + recommended next steps' },
  { type: 'summary', name: 'Summary', description: 'Condensed overview of reserved grounds and exposure' },
];

interface RoRAnalysisMemoGeneratorProps {
  /** Explicit case id (e.g. from an embedding page's case picker). Falls back
   * to the `:caseId` route param when omitted, so this component behaves
   * identically at its own route (`/attorney/case/:caseId/ror-analysis`). */
  caseId?: string;
}

export default function RoRAnalysisMemoGenerator({ caseId: propCaseId }: RoRAnalysisMemoGeneratorProps = {}) {
  const { caseId: paramCaseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const caseId = propCaseId ?? paramCaseId;

  const [depth, setDepth] = useState('full');
  const [rorDate, setRorDate] = useState('');
  const [reservedGroundsText, setReservedGroundsText] = useState('');

  const [caseData, setCaseData] = useState({
    insured_name: '', business_name: '', carrier_name: '',
    policy_number: '', claim_number: '', event_type: 'fire', event_date: '', state: '', claim_amount: 0,
  });
  const [engineClaimAmount, setEngineClaimAmount] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [loadingCase, setLoadingCase] = useState(true);
  const [generated, setGenerated] = useState<string | null>(null);
  const [reviewSections, setReviewSections] = useState<any[]>([]);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const run = async () => {
      if (!caseId) { setLoadingCase(false); return; }
      const authHeaders = await getAuthHeaders();
      if (!authHeaders.Authorization) { navigate('/attorney/login'); return; }
      try {
        const res = await fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}`, { headers: authHeaders });
        if (res.status === 401) { navigate('/attorney/login'); return; }
        if (res.ok) {
          const data = await res.json();
          setCaseData((prev) => ({
            ...prev,
            insured_name: data.client_name || '', business_name: data.business_name || data.client_name || '',
            carrier_name: data.policy_coverage?.insurer_name || data.carrier || '', claim_number: data.claim_number || '', policy_number: data.policy_coverage?.policy_number || data.policy_number || '',
            claim_amount: data.total_demand || 0, event_type: data.event_type || 'fire',
            // CaseDetailResponse exposes `event_date`, not `loss_date` — the
            // latter isn't on the response model, so this always rendered blank.
            event_date: data.event_date || '', state: data.state || '',
          }));
          if (typeof data.total_demand === 'number' && data.total_demand > 0) setEngineClaimAmount(data.total_demand);
        }
      } catch (err) { console.error('Failed to load case:', err); } finally { setLoadingCase(false); }
    };
    run();
  }, [caseId, navigate]);

  useEffect(() => {
    if (!caseId) return;
    getAuthHeaders().then((authHeaders) => fetch(`${BACKEND_URL}/api/v1/attorney/case/${caseId}/context`, { headers: authHeaders }))
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return;
        const engineTotal = typeof data.claim_amount === 'number' ? data.claim_amount : null;
        setCaseData((prev) => ({
          ...prev,
          // /context returns `event_start_date`, not `event_date`.
          event_date: prev.event_date || data.event_start_date || '',
          claim_number: prev.claim_number || data.claim_number || '', carrier_name: prev.carrier_name || data.carrier || '',
          insured_name: prev.insured_name || data.client_name || '', business_name: prev.business_name || data.business_name || '',
          policy_number: prev.policy_number || data.policy_number || '', state: prev.state || data.state || '',
          claim_amount: prev.claim_amount || engineTotal || 0,
        }));
        if (engineTotal && engineTotal > 0) setEngineClaimAmount(engineTotal);
      })
      .catch((err) => console.error('Failed to load case context:', err));
  }, [caseId]);

  const handleInputChange = (field: string, value: string | number) => setCaseData((prev) => ({ ...prev, [field]: value }));
  const linesToArray = (text: string) => text.split('\n').map((l) => l.trim()).filter(Boolean);

  const buildCalc = useCallback(() => {
    const finalAmount = engineClaimAmount ?? (caseData.claim_amount || null);
    return finalAmount ? { totals: { final_claim_amount: finalAmount } } : null;
  }, [engineClaimAmount, caseData.claim_amount]);

  const requestBody = useCallback((outputFormat: string) => ({
    depth, case_data: caseData, calculation_results: buildCalc(),
    reserved_grounds: linesToArray(reservedGroundsText).length ? linesToArray(reservedGroundsText) : null,
    ror_date: rorDate || null, output_format: outputFormat,
    report_id: caseId || null,
  }), [depth, caseData, buildCalc, reservedGroundsText, rorDate, caseId]);

  const generate = async () => {
    setLoading(true); setError(''); setGenerated(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/ror-analysis/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) }, body: JSON.stringify(requestBody('text')),
      });
      if (!res.ok) throw new Error(await responseErrorMessage(res, 'Failed to generate RoR analysis memo'));
      const data = await res.json();
      setGenerated(data.formatted_memo);
      setReviewSections(data.review_sections || []);
    } catch (err: any) { setError(err.message || 'Failed to generate RoR analysis memo'); } finally { setLoading(false); }
  };

  const downloadBlob = useCallback(async (path: string, ext: string) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/attorney-tools/ror-analysis/${path}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) }, body: JSON.stringify(requestBody('text')),
      });
      if (!res.ok) throw new Error(await responseErrorMessage(res, `Failed to download ${ext.toUpperCase()}`));
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `ror_analysis_${caseData.claim_number || 'draft'}_${new Date().toISOString().split('T')[0]}.${ext}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (err: any) { setError(err.message || `Failed to download ${ext.toUpperCase()}`); }
  }, [requestBody, caseData.claim_number]);

  const copyToClipboard = useCallback(() => {
    if (generated) { navigator.clipboard.writeText(generated); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  }, [generated]);

  const criticalReviews = reviewSections.filter((r) => (r?.severity || r?.priority || '').toString().toLowerCase() === 'critical');

  if (loadingCase) {
    return (<AttorneyLayout><div className="flex items-center justify-center min-h-[60vh]" style={{ background: 'var(--color-bg-base)' }}><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--color-brand)' }} /></div></AttorneyLayout>);
  }

  return (
    <AttorneyLayout>
      {caseId && (
        <button onClick={() => navigate(`/attorney/case/${caseId}`)} className="cs-body flex items-center gap-2 mb-6" style={{ color: 'var(--color-text-muted)' }} data-testid="back-to-case">
          <ArrowLeft className="w-4 h-4" /> Back to Case
        </button>
      )}

      <div className="mb-8">
        <h1 className="cs-display flex items-center gap-3">
          <span className="flex-shrink-0 inline-flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 11, padding: 8 }}>
            <FileSearch className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
          </span>
          RoR Coverage-Analysis Memo
        </h1>
        <p className="cs-body mt-1">Analyze a received Reservation of Rights — per-ground basis, the insured’s position, and the engine-sourced exposure at risk.</p>
        <div className="cs-chip mt-2 inline-flex items-center gap-1.5 rounded-full px-3 py-1" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }} data-testid="privilege-banner">
          <Lock className="w-3.5 h-3.5" /> Internal — Attorney Work Product
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="ror-analysis-generator">
        <div className="space-y-6">
          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-4">Analysis Depth</h3>
            <div className="space-y-2">
              {DEPTHS.map((d) => (
                <label key={d.type} className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors" style={depth === d.type ? { border: '1px solid var(--color-brand)', background: 'var(--color-needs-human-tint)' } : { border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)' }} data-testid={`depth-${d.type}`}>
                  <input type="radio" name="depth" value={d.type} checked={depth === d.type} onChange={(e) => setDepth(e.target.value)} className="sr-only" />
                  <FileSearch className="w-5 h-5" style={{ color: depth === d.type ? 'var(--color-brand)' : 'var(--color-text-muted)' }} />
                  <div><div className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 500 }}>{d.name}</div><div className="cs-meta">{d.description}</div></div>
                </label>
              ))}
            </div>
            <div className="mt-4">
              <label className="cs-label block mb-1">Date of carrier’s RoR letter</label>
              <input type="date" value={rorDate} onChange={(e) => setRorDate(e.target.value)} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="input-ror-date" />
            </div>
          </div>

          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-1">Reserved Grounds <span className="cs-meta">(one analysis block each)</span></h3>
            <p className="cs-meta mb-3">The grounds the carrier reserved, one per line. Each gets its own analysis block; absent grounds are flagged for your input.</p>
            <textarea value={reservedGroundsText} onChange={(e) => setReservedGroundsText(e.target.value)} rows={4} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} placeholder={'Protective safeguards endorsement (CP 04 11)\nLate notice of claim\nMisrepresentation in the application'} data-testid="input-reserved-grounds" />
          </div>

          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-1">Exposure at Risk</h3>
            <p className="cs-meta mb-3">From the forensic engine — the dollar exposure if the reservation ripens into a denial.</p>
            <div className="cs-figure w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="engine-total">
              <FinancialValue value={engineClaimAmount} cents />
            </div>
          </div>

          <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
            <h3 className="cs-h3 mb-4">Case Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="cs-label block mb-1">Insured Name *</label><input type="text" value={caseData.insured_name} onChange={(e) => handleInputChange('insured_name', e.target.value)} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="input-insured-name" /></div>
              <div><label className="cs-label block mb-1">Carrier Name *</label><input type="text" value={caseData.carrier_name} onChange={(e) => handleInputChange('carrier_name', e.target.value)} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="input-carrier-name" /></div>
              <div><label className="cs-label block mb-1">Policy Number</label><input type="text" value={caseData.policy_number} onChange={(e) => handleInputChange('policy_number', e.target.value)} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="input-policy-number" /></div>
              <div><label className="cs-label block mb-1">Claim Number</label><input type="text" value={caseData.claim_number} onChange={(e) => handleInputChange('claim_number', e.target.value)} className="cs-body w-full px-3 py-2 rounded-lg" style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)' }} data-testid="input-claim-number" /></div>
            </div>
          </div>

          <button onClick={generate} disabled={loading} className="cs-body w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg disabled:cursor-not-allowed" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500, opacity: loading ? 0.5 : 1 }} data-testid="generate-ror-analysis">
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating…</> : <><FileSearch className="w-5 h-5" /> Generate Coverage Analysis</>}
          </button>
          {error && <div className="cs-body rounded-lg px-4 py-3" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-needs-human-text)' }} data-testid="ror-analysis-error">{error}</div>}
        </div>

        <div className="space-y-6">
          {criticalReviews.length > 0 && (
            <div style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 14, padding: 20 }} data-testid="ror-analysis-critical-reviews">
              <div className="flex items-start gap-3"><ShieldAlert className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div><h4 className="cs-body" style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>Requires attorney input</h4>
                  <p className="cs-meta mt-1" style={{ color: 'var(--color-needs-human-text)' }}>Coverage positions and strength assessments the memo cannot infer — Supply before relying on it.</p>
                  <ul className="mt-2 space-y-1">{criticalReviews.map((r, i) => (<li key={i} className="cs-meta flex items-start gap-2" style={{ color: 'var(--color-needs-human-text)' }}><AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span><strong>{r.section || r.title || 'Review'}:</strong> {r.message || r.note || r.description}</span></li>))}</ul>
                </div>
              </div>
            </div>
          )}

          {generated ? (
            <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }} data-testid="ror-analysis-preview">
              <div className="flex items-center justify-between mb-4">
                <h3 className="cs-h3">Coverage-Analysis Memo</h3>
                <div className="flex items-center gap-2">
                  <button onClick={copyToClipboard} className="cs-meta inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }} data-testid="copy-ror-analysis">{copied ? <><Check className="w-4 h-4" style={{ color: 'var(--color-verified)' }} /> Copied</> : <><Copy className="w-4 h-4" /> Copy</>}</button>
                  <button onClick={() => downloadBlob('export/pdf', 'pdf')} className="cs-meta inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }} data-testid="download-pdf"><Download className="w-4 h-4" /> PDF</button>
                  <button onClick={() => downloadBlob('export/docx', 'docx')} className="cs-meta inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg" style={{ border: '1px solid var(--color-border)', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }} data-testid="download-docx"><Download className="w-4 h-4" /> DOCX</button>
                </div>
              </div>
              <pre className="cs-figure whitespace-pre-wrap rounded-lg p-4 max-h-[70vh] overflow-auto" style={{ background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>{generated}</pre>
            </div>
          ) : (
            <div className="cs-body p-12 text-center" style={{ background: 'var(--color-bg-surface)', border: '1px dashed var(--color-border)', borderRadius: 14, color: 'var(--color-text-muted)' }} data-testid="ror-analysis-empty">
              <FileSearch className="w-10 h-10 mx-auto mb-3 opacity-50" /><p>Fill in the reserved grounds and generate a coverage-analysis memo.</p>
            </div>
          )}
        </div>
      </div>
    </AttorneyLayout>
  );
}
